import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, PropsWithChildren, useContext, useEffect } from 'react';
import { useRefWithCurrentValue } from 'record-it-components/hooks/useRefWithCurrentValue';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PageLoadingOverlayContext } from '../context/PageLoadingOverlayContext';
import { useEntityManager } from '../hooks/entity/useEntityManager';
import { useAuth } from '../hooks/useAuth';
import { useCurrentUser } from '../hooks/useCurrentUser';

/**
 * A component which displays the AppPageLoadingOverlay when the user is logged in but the currentUser is not available.
 * This happens when the first actualization is not done yet.
 */
export const AppActualizationOverlayController: FunctionComponent<PropsWithChildren> = React.memo(props => {
  const $ = _c(11);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    children
  } = t0;
  const {
    entityManager
  } = useEntityManager();
  const {
    isLoggedIn
  } = useAuth();
  const currentUserRef = useRefWithCurrentValue(useCurrentUser().currentUser);
  const {
    startLoading,
    addLoadingMessage
  } = useContext(PageLoadingOverlayContext);
  let t1;
  let t2;
  if ($[2] !== addLoadingMessage || $[3] !== currentUserRef || $[4] !== entityManager || $[5] !== isLoggedIn || $[6] !== startLoading) {
    t1 = () => {
      if (!isLoggedIn || currentUserRef.current) {
        return;
      }
      const loadingDisposable = startLoading();
      addLoadingMessage("synchronizing");
      const hooksDisposable = entityManager.entityActualization.registerHooks({
        afterActualization: () => {
          hooksDisposable.dispose();
          loadingDisposable.dispose();
        }
      });
      return () => {
        hooksDisposable.dispose();
        loadingDisposable.dispose();
      };
    };
    t2 = [entityManager, isLoggedIn, currentUserRef, startLoading, addLoadingMessage];
    $[2] = addLoadingMessage;
    $[3] = currentUserRef;
    $[4] = entityManager;
    $[5] = isLoggedIn;
    $[6] = startLoading;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  if ($[9] !== children) {
    t3 = <>{children}</>;
    $[9] = children;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
});